import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package, Anchor, Warehouse } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Signed in");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Left hero */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-slate-100 p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage:
            "url(https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHszfHx3YXJlaG91c2UlMjBjYXJnbyUyMGxvZ2lzdGljcyUyMGNvbnRhaW5lciUyMGludmVudG9yeXxlbnwwfHx8fDE3ODkwMDc4NzF8MA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover", backgroundPosition: "center" }}/>
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-sky-500 grid place-items-center">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">CargoLedger</div>
            <div className="caption text-slate-400">CN → MY Inventory Hub</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight max-w-lg">
            From <span className="text-sky-400">Guangzhou dock</span> to <span className="text-emerald-400">Malaysia rack</span>&mdash;one ledger.
          </h1>
          <p className="mt-6 text-slate-300 max-w-md">
            Record purchases in CNY & RM, group items by warehouse tracking code, verify CBM & quantities on arrival, and audit every piece's true landed cost.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <Anchor className="w-5 h-5 text-sky-400" />
              <div className="mt-2 font-mono text-xs text-slate-400 uppercase tracking-widest">Track</div>
              <div className="font-heading font-semibold">Invoices · CBM · ETA</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <Warehouse className="w-5 h-5 text-emerald-400" />
              <div className="mt-2 font-mono text-xs text-slate-400 uppercase tracking-widest">Verify</div>
              <div className="font-heading font-semibold">Qty · Photos · Rack</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 caption">© {new Date().getFullYear()} CargoLedger · Built for cross-border operators</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="caption">Sign in</div>
            <h2 className="font-heading text-3xl font-bold mt-1">Welcome back</h2>
            <p className="text-slate-500 mt-2 text-sm">Access the operations console.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <div className="mt-8 text-xs text-slate-400 caption">
            Admin can create staff accounts inside the app.
          </div>
        </form>
      </div>
    </div>
  );
}
